/**
 * NoticeCounterSignModal (S6.1) — capture the factory 黃單 counter-sign on a
 * sent 出貨通知.
 *
 * Per-line counter-signed quantities default to the white-slip estimates;
 * saving acknowledges the notice and feeds the A2 出貨通知再確認 quantity
 * buckets server-side (checkpoint label rendered bilingually from
 * `ofcVocabulary.ts` — never a bare A-code).
 */

import { useEffect, useState } from 'react';
import { App, Alert, Button, InputNumber, Modal, Table, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatVendorCheckpoint } from '@/constants/ofcVocabulary';
import { extractApiError } from '@/hooks/useSalesOrders';
import { useShippingNoticeMutations, useShippingPoOptions } from '@/hooks/useShipping';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import type { ShippingNotice } from '@/types/api/shipping';

interface CounterSignLine {
  poLineId: string;
  sku: string;
  qtyEstimated: number;
}

export interface NoticeCounterSignModalProps {
  open: boolean;
  /** The sent notice being counter-signed (null while closed). */
  notice: ShippingNotice | null;
  onClose: () => void;
  /** Called after a successful counter-sign (page refreshes its list). */
  onDone: () => void;
}

const NoticeCounterSignModal = ({ open, notice, onClose, onDone }: NoticeCounterSignModalProps) => {
  const { t, i18n } = useTranslation(['shipping', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const { purchaseOrders } = useShippingPoOptions();
  const { counterSignNotice } = useShippingNoticeMutations();

  const [confirmed, setConfirmed] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);

  // Counter-signed quantities default to the white-slip estimates.
  useEffect(() => {
    setConfirmed(
      Object.fromEntries(
        (notice?.lines ?? []).map((line) => [
          line.poLineId,
          line.qtyConfirmed > 0 ? line.qtyConfirmed : line.qtyEstimated,
        ])
      )
    );
  }, [notice]);

  /** Display SKU for a PO line (join over the mock-scale PO options). */
  const skuForPoLine = (poLineId: string): string => {
    for (const po of purchaseOrders) {
      const line = po.lines.find((candidate) => candidate.id === poLineId);
      if (line) return line.sku;
    }
    return poLineId;
  };

  const lines: CounterSignLine[] = (notice?.lines ?? []).map((line) => ({
    poLineId: line.poLineId,
    sku: skuForPoLine(line.poLineId),
    qtyEstimated: line.qtyEstimated,
  }));

  const checkpointLabel = formatVendorCheckpoint(
    'A2',
    i18n.language.startsWith('zh') ? 'zh' : 'en'
  );

  const onSubmit = async () => {
    if (!notice) return;
    setSubmitting(true);
    try {
      await counterSignNotice(
        notice.id,
        notice.lines.map((line) => ({
          poLineId: line.poLineId,
          qtyConfirmed: confirmed[line.poLineId] ?? line.qtyEstimated,
        }))
      );
      message.success(t('shipping:notice.messages.counterSigned'));
      onClose();
      onDone();
    } catch (error) {
      const body = extractApiError(error);
      message.error(body.message || t('common:messages.updateFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('shipping:notice.counterSign.title', { noticeNumber: notice?.noticeNumber ?? '' })}
      onCancel={onClose}
      width={800}
      footer={[
        <Button
          key="cancel"
          size="large"
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          onClick={onClose}
        >
          {t('common:actions.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          size="large"
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          loading={submitting}
          onClick={() => void onSubmit()}
        >
          {t('shipping:notice.counterSign.submit')}
        </Button>,
      ]}
    >
      <Alert
        className="tw-mb-4"
        type="info"
        showIcon
        message={t('shipping:notice.counterSign.hint', { checkpoint: checkpointLabel })}
      />
      <div style={{ ...formStyles.fieldContainerStyle, padding: '20px' }}>
        <Table<CounterSignLine>
          size="small"
          rowKey="poLineId"
          dataSource={lines}
          pagination={false}
          columns={[
            {
              title: t('shipping:notice.modal.sku'),
              dataIndex: 'sku',
              key: 'sku',
              render: (value: string) => (
                <span style={{ fontWeight: token.fontWeightStrong }}>{value}</span>
              ),
            },
            {
              title: t('shipping:notice.counterSign.qtyEstimated'),
              dataIndex: 'qtyEstimated',
              key: 'qtyEstimated',
              align: 'right',
              render: (value: number) => (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
              ),
            },
            {
              title: t('shipping:notice.counterSign.qtyConfirmed'),
              key: 'qtyConfirmed',
              width: 160,
              render: (_: unknown, line) => (
                <InputNumber
                  min={0}
                  value={confirmed[line.poLineId] ?? line.qtyEstimated}
                  size="large"
                  style={{ width: '100%' }}
                  aria-label={t('shipping:notice.counterSign.qtyConfirmedFor', { sku: line.sku })}
                  onChange={(value) =>
                    setConfirmed((prev) => ({ ...prev, [line.poLineId]: value ?? 0 }))
                  }
                />
              ),
            },
          ]}
        />
      </div>
    </Modal>
  );
};

export default NoticeCounterSignModal;
